import React, { useState, useEffect, useRef } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";
import { Plus, Edit, Trash2, Users, Upload, Download, Search, FileText, AlertTriangle, RefreshCw, Building2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Partners({ user, onLogout }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importId, setImportId] = useState(null);
  const [currentUserPartner, setCurrentUserPartner] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [userFilter, setUserFilter] = useState("all"); // "all", "with_user", "without_user"
  const [distributorFilter, setDistributorFilter] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [distributorsList, setDistributorsList] = useState([]);
  const fileInputRef = useRef(null);
  
  // Verificar se é usuário de cadastro de parceiros
  const isPartnerRegistration = user?.role === 'partner_registration';
  const [formData, setFormData] = useState({
    registration_type: "PF",
    name: "",
    email: "",
    phone: "",
    cpf_cnpj: "",
    estado_civil: "",
    profissao: "",
    legal_representative_name: "",
    legal_representative_cpf: "",
    distributor: "",
    street: "",
    number: "",
    neighborhood: "",
    city: "",
    state: "",
    zip_code: "",
    level: 1,
    parent_id: ""
  });
  const [documents, setDocuments] = useState({
    rg_cpf_file: null,
    energy_bill_file: null,
    cnpj_card_file: null
  });

  useEffect(() => {
    fetchPartners();
    fetchDistributors();
    if (user?.role !== 'admin') {
      fetchCurrentUserPartner();
    }
  }, [user]);

  const fetchDistributors = async () => {
    try {
      const res = await axiosInstance.get("/distributor-settings");
      const list = Array.isArray(res.data)
        ? res.data.map(d => (d.distributor || '').trim()).filter(Boolean)
        : [];
      // Case-insensitive dedupe (mantém primeira ocorrência canônica)
      const seen = new Set();
      const unique = [];
      list.forEach(n => {
        const k = n.toUpperCase();
        if (!seen.has(k)) { seen.add(k); unique.push(n); }
      });
      unique.sort((a, b) => a.localeCompare(b, 'pt-BR'));
      setDistributorsList(unique);
    } catch (e) {
      console.warn("Falha ao carregar distribuidoras:", e?.message || e);
      // fallback: dropdown fica vazio (usuário será orientado a cadastrar em Configurações)
    }
  };

  // Recarregar quando o filtro de usuário mudar
  useEffect(() => {
    setCurrentPage(1);
    fetchPartners();
  }, [userFilter]);

  const fetchCurrentUserPartner = async () => {
    try {
      const response = await axiosInstance.get(`/partners/by-user/${user.id}`);
      setCurrentUserPartner(response.data);
    } catch (error) {
      console.log("User doesn't have a partner yet");
    }
  };

  const fetchPartners = async () => {
    try {
      const params = { limit: 5000 };
      if (userFilter !== "all") {
        params.user_filter = userFilter;
      }
      const response = await axiosInstance.get("/partners", { params });
      
      // Backend já retorna has_rg_cpf, has_energy_bill, has_cnpj_card, has_user
      setPartners(response.data);
    } catch (error) {
      toast.error("Erro ao carregar parceiros");
      console.error("Erro ao carregar parceiros:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar documentos obrigatórios no cadastro
    if (!editingPartner) {
      if (!documents.rg_cpf_file) {
        toast.error("RG/CPF é obrigatório");
        return;
      }
      if (!documents.energy_bill_file) {
        toast.error("Conta de Energia é obrigatória");
        return;
      }
      if (formData.registration_type === "PJ" && !documents.cnpj_card_file) {
        toast.error("Cartão CNPJ é obrigatório para Pessoa Jurídica");
        return;
      }
    }
    
    try {
      if (editingPartner) {
        // Update existing partner (sem arquivos por enquanto)
        const payload = {
          registration_type: formData.registration_type,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          cpf_cnpj: formData.cpf_cnpj,
          estado_civil: formData.estado_civil || null,
          profissao: formData.profissao || null,
          legal_representative_name: formData.legal_representative_name || null,
          legal_representative_cpf: formData.legal_representative_cpf || null,
          distributor: formData.distributor,
          street: formData.street,
          number: formData.number,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code || null,
          parent_id: formData.parent_id || null,  // ✅ CORRIGIDO: usar formData
          level: formData.level || 1  // ✅ ADICIONADO: enviar nível
        };
        
        console.log('📤 Enviando atualização de parceiro:', payload);
        
        await axiosInstance.put(`/partners/${editingPartner.id}`, payload);
        toast.success("Parceiro atualizado com sucesso!");
      } else {
        // Create new partner with files
        const formDataToSend = new FormData();
        formDataToSend.append('registration_type', formData.registration_type);
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone);
        formDataToSend.append('cpf_cnpj', formData.cpf_cnpj);
        formDataToSend.append('estado_civil', formData.estado_civil || '');
        formDataToSend.append('profissao', formData.profissao || '');
        formDataToSend.append('legal_representative_name', formData.legal_representative_name || '');
        formDataToSend.append('legal_representative_cpf', formData.legal_representative_cpf || '');
        formDataToSend.append('distributor', formData.distributor);
        formDataToSend.append('street', formData.street);
        formDataToSend.append('number', formData.number);
        formDataToSend.append('neighborhood', formData.neighborhood);
        formDataToSend.append('city', formData.city);
        formDataToSend.append('state', formData.state);
        formDataToSend.append('zip_code', formData.zip_code || '');
        
        // Se for usuário de cadastro de parceiros, definir level = 1
        if (isPartnerRegistration) {
          formDataToSend.append('level', '1');
        }
        
        // Adicionar arquivos
        formDataToSend.append('rg_cpf_file', documents.rg_cpf_file);
        formDataToSend.append('energy_bill_file', documents.energy_bill_file);
        if (documents.cnpj_card_file) {
          formDataToSend.append('cnpj_card_file', documents.cnpj_card_file);
        }
        
        await axiosInstance.post("/partners", formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success("Parceiro cadastrado com sucesso!");
        // Voltar para página 1 para ver o novo parceiro
        setCurrentPage(1);
      }
      
      setDialogOpen(false);
      setEditingPartner(null);
      setFormData({
        registration_type: "PF",
        name: "",
        email: "",
        phone: "",
        cpf_cnpj: "",
        estado_civil: "",
        profissao: "",
        legal_representative_name: "",
        legal_representative_cpf: "",
        distributor: "",
        street: "",
        number: "",
        neighborhood: "",
        city: "",
        state: "",
        zip_code: "",
        level: 1,
        parent_id: ""
      });
      setDocuments({
        rg_cpf_file: null,
        energy_bill_file: null,
        cnpj_card_file: null
      });
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Erro ao ${editingPartner ? 'atualizar' : 'cadastrar'} parceiro`);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      registration_type: partner.registration_type,
      name: partner.name,
      email: partner.email,
      phone: partner.phone || "",
      cpf_cnpj: partner.cpf_cnpj,
      estado_civil: partner.estado_civil || "",
      profissao: partner.profissao || "",
      legal_representative_name: partner.legal_representative_name || "",
      legal_representative_cpf: partner.legal_representative_cpf || "",
      distributor: partner.distributor || "",
      street: partner.street || "",
      number: partner.number || "",
      neighborhood: partner.neighborhood || "",
      city: partner.city || "",
      state: partner.state || "",
      zip_code: partner.zip_code || "",
      level: partner.level || 1,
      parent_id: partner.parent_id || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este parceiro?")) return;
    try {
      await axiosInstance.delete(`/partners/${id}`);
      toast.success("Parceiro excluído com sucesso!");
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir parceiro");
    }
  };

  const handleDeleteAllPartners = async () => {
    try {
      const response = await axiosInstance.delete("/partners/all");
      toast.success(response.data.message);
      setDeleteAllConfirm(false);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao deletar parceiros");
    }
  };

  const handleCleanupPartnersData = async () => {
    if (!window.confirm('Deseja limpar dados corrompidos dos parceiros (remover apóstrofos de CPF, telefone, etc.)?')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post("/partners/cleanup-data");
      toast.success(`Limpeza concluída! ${response.data.updated} registros atualizados.`);
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao limpar dados");
    }
  };

  const handleStatusChange = async (partnerId, newStatus) => {
    try {
      await axiosInstance.put(`/partners/${partnerId}/status?status=${newStatus}`);
      toast.success("Status atualizado com sucesso!");
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar status");
    }
  };

  const handleDownloadContract = async (partnerId, partnerName) => {
    try {
      const response = await axiosInstance.get(`/partners/${partnerId}/contract`, {
        responseType: 'blob'
      });
      
      // Usar o Content-Type da resposta para criar o Blob corretamente
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrato_Parceria_${partnerName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contrato baixado com sucesso!");
    } catch (error) {
      console.error("Error downloading contract:", error);
      toast.error("Erro ao baixar contrato");
    }
  };

  const handleDownloadDocument = async (partner, docType) => {
    try {
      // Download do backend (que também excluirá o documento)
      const response = await axiosInstance.get(
        `/partners/${partner.id}/document/${docType}`,
        { responseType: 'blob' }
      );
      
      // Obter o Content-Type da resposta para criar o Blob corretamente
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Obter filename do header ou gerar um
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `Documento_${partner.name.replace(/\s+/g, '_')}`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link - usando o Content-Type correto para evitar download como TXT
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Documento baixado e excluído com sucesso!");
      
      // Recarregar lista de parceiros para atualizar status dos documentos
      fetchPartners();
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error(error.response?.data?.detail || "Erro ao baixar documento");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportErrors([]);
    setImportId(null);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosInstance.post("/partners/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`${response.data.imported} parceiros importados com sucesso!`);
      if (response.data.errors && response.data.errors.length > 0) {
        setImportErrors(response.data.errors);
        setImportId(response.data.import_id);
        toast.warning(`${response.data.errors.length} erros encontrados. Clique em "Baixar Erros" para ver detalhes.`);
      }
      fetchPartners();
    } catch (error) {
      toast.error("Erro ao importar CSV");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadErrors = async () => {
    if (!importId) return;

    try {
      const response = await axiosInstance.get(`/partners/export-import-errors/${importId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `erros_importacao_parceiros_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Erros exportados com sucesso");
    } catch (error) {
      console.error("Error downloading errors:", error);
      toast.error("Erro ao baixar arquivo de erros");
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      const params = {};
      if (userFilter !== "all") {
        params.user_filter = userFilter;
      }
      const response = await axiosInstance.get("/partners/export-csv", {
        params,
        responseType: "blob"
      });
      
      // Add filter suffix to filename
      const filterSuffix = userFilter === "with_user" ? "_com_usuario" : 
                          userFilter === "without_user" ? "_sem_usuario" : "";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `parceiros${filterSuffix}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/partners/template", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_parceiros.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Modelo baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar modelo");
    }
  };



  const getParentName = (parentId) => {
    const parent = partners.find(p => p.id === parentId);
    return parent ? parent.name : "-";
  };

  const getLevelBadge = (level) => {
    const colors = {
      1: "bg-emerald-100 text-emerald-700",
      2: "bg-blue-100 text-blue-700",
      3: "bg-purple-100 text-purple-700"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[level]}`}>
        Nível {level}
      </span>
    );
  };

  // Se for usuário de cadastro de parceiros, renderizar apenas o formulário
  if (isPartnerRegistration) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6 max-w-4xl mx-auto p-6">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Cadastro de Parceiro</h1>
            <p className="text-gray-600">Preencha os dados abaixo para se cadastrar como parceiro nível 1</p>
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Informações do Parceiro</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Copiado o formulário completo do Dialog */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipo de Cadastro */}
                <div className="space-y-2">
                  <Label htmlFor="registration_type">Tipo de Cadastro *</Label>
                  <Select
                    value={formData.registration_type}
                    onValueChange={(value) => setFormData({ ...formData, registration_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nome */}
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Parceiro *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                {/* CPF ou CNPJ */}
                <div className="space-y-2">
                  <Label htmlFor="cpf_cnpj">
                    {formData.registration_type === "PF" ? "CPF *" : "CNPJ *"}
                  </Label>
                  <Input
                    id="cpf_cnpj"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    placeholder={formData.registration_type === "PF" ? "000.000.000-00" : "00.000.000/0000-00"}
                    required
                  />
                </div>

                {/* Estado Civil e Profissão (para PF e Representante Legal PJ) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estado_civil">Estado Civil</Label>
                    <Input
                      id="estado_civil"
                      value={formData.estado_civil}
                      onChange={(e) => setFormData({ ...formData, estado_civil: e.target.value })}
                      placeholder="Ex: Solteiro(a), Casado(a)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profissao">Profissão</Label>
                    <Input
                      id="profissao"
                      value={formData.profissao}
                      onChange={(e) => setFormData({ ...formData, profissao: e.target.value })}
                      placeholder="Ex: Engenheiro, Empresário"
                    />
                  </div>
                </div>

                {/* Campos PJ - Representante Legal */}
                {formData.registration_type === "PJ" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="legal_representative_name">Representante Legal</Label>
                      <Input
                        id="legal_representative_name"
                        value={formData.legal_representative_name}
                        onChange={(e) => setFormData({ ...formData, legal_representative_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="legal_representative_cpf">CPF do Representante Legal</Label>
                      <Input
                        id="legal_representative_cpf"
                        value={formData.legal_representative_cpf}
                        onChange={(e) => setFormData({ ...formData, legal_representative_cpf: e.target.value })}
                        placeholder="000.000.000-00"
                      />
                    </div>
                  </>
                )}

                {/* Distribuidora */}
                <div className="space-y-2">
                  <Label htmlFor="distributor">Distribuidora *</Label>
                  <Select
                    value={formData.distributor}
                    onValueChange={(value) => setFormData({ ...formData, distributor: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a distribuidora" />
                    </SelectTrigger>
                    <SelectContent>
                      {distributorsList.length === 0 ? (
                        <div className="p-2 text-xs text-gray-500">Nenhuma distribuidora cadastrada. Cadastre em Configurações → Distribuidoras.</div>
                      ) : (
                        distributorsList.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Telefone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Endereço */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Endereço</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="street">Rua *</Label>
                      <Input
                        id="street"
                        value={formData.street}
                        onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="number">Número *</Label>
                      <Input
                        id="number"
                        value={formData.number}
                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro *</Label>
                      <Input
                        id="neighborhood"
                        value={formData.neighborhood}
                        onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="state">Estado *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => setFormData({ ...formData, state: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SC">Santa Catarina</SelectItem>
                          <SelectItem value="PR">Paraná</SelectItem>
                          <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                          <SelectItem value="SP">São Paulo</SelectItem>
                          <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                          <SelectItem value="MG">Minas Gerais</SelectItem>
                          <SelectItem value="BA">Bahia</SelectItem>
                          <SelectItem value="GO">Goiás</SelectItem>
                          <SelectItem value="DF">Distrito Federal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">CEP *</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="00000-000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Seção de Documentos - Obrigatórios */}
                <div className="space-y-4 border-t pt-4 mt-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Documentos (Obrigatórios)
                  </h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="rg_cpf_file">
                      RG/CPF <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="rg_cpf_file"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setDocuments({...documents, rg_cpf_file: e.target.files[0]})}
                      required
                    />
                    <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="energy_bill_file">
                      Conta de Energia <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="energy_bill_file"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setDocuments({...documents, energy_bill_file: e.target.files[0]})}
                      required
                    />
                    <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                  </div>

                  {formData.registration_type === "PJ" && (
                    <div className="space-y-2">
                      <Label htmlFor="cnpj_card_file">
                        Cartão CNPJ <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="cnpj_card_file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setDocuments({...documents, cnpj_card_file: e.target.files[0]})}
                        required
                      />
                      <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-6 text-lg font-semibold">
                  Cadastrar Parceiro
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-2">Parceiros Comerciais</h1>
            <p className="text-gray-600">Gerencie a rede de parceiros (até 3 níveis)</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'admin' && (
              <>
                <Button
                  onClick={handleDownloadTemplate}
                  className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Modelo
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImportCSV}
                  accept=".csv"
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? "Importando..." : "Importar CSV"}
                </Button>
                {importErrors.length > 0 && (
                  <Button
                    onClick={handleDownloadErrors}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Erros ({importErrors.length})
                  </Button>
                )}
                <Button
                  onClick={handleExportCSV}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
                <Button
                  onClick={() => setDeleteAllConfirm(true)}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Todos
                </Button>
                <Button
                  onClick={handleCleanupPartnersData}
                  variant="outline"
                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                  title="Remove apóstrofos de CPF, telefone e outros campos que podem ter sido corrompidos na importação"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Limpar Dados
                </Button>
              </>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingPartner(null);
                setFormData({
                  registration_type: "PF",
                  name: "",
                  email: "",
                  phone: "",
                  cpf_cnpj: "",
                  estado_civil: "",
                  profissao: "",
                  legal_representative_name: "",
                  legal_representative_cpf: "",
                  distributor: "",
                  street: "",
                  number: "",
                  neighborhood: "",
                  city: "",
                  state: ""
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-partner-button" className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Parceiro
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPartner ? "Editar Parceiro" : "Cadastrar Novo Parceiro"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipo de Cadastro */}
                <div className="space-y-2">
                  <Label htmlFor="registration_type">Tipo de Cadastro *</Label>
                  <Select
                    value={formData.registration_type}
                    onValueChange={(value) => setFormData({ ...formData, registration_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nome */}
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Parceiro *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                {/* CPF ou CNPJ */}
                <div className="space-y-2">
                  <Label htmlFor="cpf_cnpj">
                    {formData.registration_type === "PF" ? "CPF *" : "CNPJ *"}
                  </Label>
                  <Input
                    id="cpf_cnpj"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    placeholder={formData.registration_type === "PF" ? "000.000.000-00" : "00.000.000/0000-00"}
                    required
                  />
                </div>

                {/* Estado Civil e Profissão (para PF e Representante Legal PJ) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="estado_civil">Estado Civil</Label>
                    <Input
                      id="estado_civil"
                      value={formData.estado_civil}
                      onChange={(e) => setFormData({ ...formData, estado_civil: e.target.value })}
                      placeholder="Ex: Solteiro(a), Casado(a)"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profissao">Profissão</Label>
                    <Input
                      id="profissao"
                      value={formData.profissao}
                      onChange={(e) => setFormData({ ...formData, profissao: e.target.value })}
                      placeholder="Ex: Engenheiro, Empresário"
                    />
                  </div>
                </div>

                {/* Campos PJ - Representante Legal */}
                {formData.registration_type === "PJ" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="legal_representative_name">Representante Legal</Label>
                      <Input
                        id="legal_representative_name"
                        value={formData.legal_representative_name}
                        onChange={(e) => setFormData({ ...formData, legal_representative_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="legal_representative_cpf">CPF do Representante Legal</Label>
                      <Input
                        id="legal_representative_cpf"
                        value={formData.legal_representative_cpf}
                        onChange={(e) => setFormData({ ...formData, legal_representative_cpf: e.target.value })}
                        placeholder="000.000.000-00"
                      />
                    </div>
                  </>
                )}

                {/* Distribuidora */}
                <div className="space-y-2">
                  <Label htmlFor="distributor">Distribuidora *</Label>
                  <Select
                    value={formData.distributor}
                    onValueChange={(value) => setFormData({ ...formData, distributor: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a distribuidora" />
                    </SelectTrigger>
                    <SelectContent>
                      {distributorsList.length === 0 ? (
                        <div className="p-2 text-xs text-gray-500">Nenhuma distribuidora cadastrada. Cadastre em Configurações → Distribuidoras.</div>
                      ) : (
                        distributorsList.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Telefone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(00) 00000-0000"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Endereço */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Endereço</h3>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="street">Rua *</Label>
                      <Input
                        id="street"
                        value={formData.street}
                        onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="number">Número *</Label>
                      <Input
                        id="number"
                        value={formData.number}
                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="neighborhood">Bairro *</Label>
                      <Input
                        id="neighborhood"
                        value={formData.neighborhood}
                        onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Cidade *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="state">Estado *</Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => setFormData({ ...formData, state: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AC">Acre</SelectItem>
                          <SelectItem value="AL">Alagoas</SelectItem>
                          <SelectItem value="AP">Amapá</SelectItem>
                          <SelectItem value="AM">Amazonas</SelectItem>
                          <SelectItem value="BA">Bahia</SelectItem>
                          <SelectItem value="CE">Ceará</SelectItem>
                          <SelectItem value="DF">Distrito Federal</SelectItem>
                          <SelectItem value="ES">Espírito Santo</SelectItem>
                          <SelectItem value="GO">Goiás</SelectItem>
                          <SelectItem value="MA">Maranhão</SelectItem>
                          <SelectItem value="MT">Mato Grosso</SelectItem>
                          <SelectItem value="MS">Mato Grosso do Sul</SelectItem>
                          <SelectItem value="MG">Minas Gerais</SelectItem>
                          <SelectItem value="PA">Pará</SelectItem>
                          <SelectItem value="PB">Paraíba</SelectItem>
                          <SelectItem value="PR">Paraná</SelectItem>
                          <SelectItem value="PE">Pernambuco</SelectItem>
                          <SelectItem value="PI">Piauí</SelectItem>
                          <SelectItem value="RJ">Rio de Janeiro</SelectItem>
                          <SelectItem value="RN">Rio Grande do Norte</SelectItem>
                          <SelectItem value="RS">Rio Grande do Sul</SelectItem>
                          <SelectItem value="RO">Rondônia</SelectItem>
                          <SelectItem value="RR">Roraima</SelectItem>
                          <SelectItem value="SC">Santa Catarina</SelectItem>
                          <SelectItem value="SP">São Paulo</SelectItem>
                          <SelectItem value="SE">Sergipe</SelectItem>
                          <SelectItem value="TO">Tocantins</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">CEP *</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        placeholder="00000-000"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Campos Admin - Nível e Parceiro Superior */}
                {user?.role === 'admin' && editingPartner && (
                  <div className="space-y-4 border-t pt-4 mt-4 bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-lg text-blue-700">
                      Configurações Administrativas
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="level">Nível *</Label>
                        <Select
                          value={String(formData.level)}
                          onValueChange={(value) => setFormData({ ...formData, level: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Nível 1</SelectItem>
                            <SelectItem value="2">Nível 2</SelectItem>
                            <SelectItem value="3">Nível 3</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-600">Nível hierárquico do parceiro</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="parent_id">Parceiro Superior</Label>
                        <Select
                          value={formData.parent_id || "none"}
                          onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sem parceiro superior" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sem parceiro superior</SelectItem>
                            {partners
                              .filter(p => p.id !== editingPartner.id)
                              .map(p => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name} (Nível {p.level})
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-600">Deixe vazio para nível 1 independente</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Seção de Documentos - Obrigatórios */}
                {!editingPartner && (
                  <div className="space-y-4 border-t pt-4 mt-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Upload className="w-5 h-5" />
                      Documentos (Obrigatórios)
                    </h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="rg_cpf_file">
                        RG/CPF <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="rg_cpf_file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setDocuments({...documents, rg_cpf_file: e.target.files[0]})}
                        required
                      />
                      <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="energy_bill_file">
                        Conta de Energia <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="energy_bill_file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setDocuments({...documents, energy_bill_file: e.target.files[0]})}
                        required
                      />
                      <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                    </div>

                    {formData.registration_type === "PJ" && (
                      <div className="space-y-2">
                        <Label htmlFor="cnpj_card_file">
                          Cartão CNPJ <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="cnpj_card_file"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setDocuments({...documents, cnpj_card_file: e.target.files[0]})}
                          required
                        />
                        <p className="text-xs text-gray-500">Formatos aceitos: PDF, JPG, PNG</p>
                      </div>
                    )}
                  </div>
                )}

                <Button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-600">
                  {editingPartner ? "Atualizar Parceiro" : "Cadastrar Parceiro"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Lista de Parceiros
            </CardTitle>
            {user?.role === 'admin' && (
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-gray-400" />
                  <Input
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-md"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <Select value={userFilter} onValueChange={setUserFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filtrar por usuário" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os parceiros</SelectItem>
                      <SelectItem value="with_user">Com usuário criado</SelectItem>
                      <SelectItem value="without_user">Sem usuário criado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <Select value={distributorFilter} onValueChange={(v) => { setDistributorFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-[200px]" data-testid="distributor-filter">
                      <SelectValue placeholder="Filtrar por distribuidora" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas distribuidoras</SelectItem>
                      {[...new Set(partners.map(p => p.distributor).filter(Boolean))].sort().map(dist => (
                        <SelectItem key={dist} value={dist}>{dist}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Carregando...</div>
            ) : partners.filter(p => 
                (searchTerm === "" || 
                p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
                (distributorFilter === "all" || p.distributor === distributorFilter)
              ).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum parceiro encontrado.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>CPF/CNPJ</TableHead>
                    <TableHead>Distribuidora</TableHead>
                    <TableHead>Nível</TableHead>
                    <TableHead>Superior</TableHead>
                    <TableHead>Status</TableHead>
                    {user?.role === 'admin' && <TableHead>Ações</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.filter(p => 
                    (searchTerm === "" || 
                    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    p.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
                    (distributorFilter === "all" || p.distributor === distributorFilter)
                  )
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((partner) => (
                    <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                      <TableCell>
                        <span className="text-xs font-semibold">
                          {partner.registration_type || "PF"}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{partner.name}</TableCell>
                      <TableCell>{partner.email}</TableCell>
                      <TableCell>{partner.cpf_cnpj || partner.cpf || "-"}</TableCell>
                      <TableCell>{partner.distributor || "-"}</TableCell>
                      <TableCell>{getLevelBadge(partner.level)}</TableCell>
                      <TableCell>{getParentName(partner.parent_id)}</TableCell>
                      <TableCell>
                        {user?.role === 'admin' ? (
                          <select
                            data-testid={`status-select-${partner.id}`}
                            className={`px-2 py-1 rounded-md text-xs font-semibold border-0 cursor-pointer ${
                              partner.status === 'active' ? 'bg-green-100 text-green-700' : 
                              partner.status === 'sent_for_signature' ? 'bg-blue-100 text-blue-700' :
                              partner.status === 'suspended' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}
                            value={partner.status}
                            onChange={(e) => handleStatusChange(partner.id, e.target.value)}
                          >
                            <option value="pending">Pendente</option>
                            <option value="sent_for_signature">Enviado para Assinatura</option>
                            <option value="active">Ativo</option>
                            <option value="suspended">Suspenso</option>
                          </select>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            partner.status === 'active' ? 'bg-green-100 text-green-700' : 
                            partner.status === 'sent_for_signature' ? 'bg-blue-100 text-blue-700' :
                            partner.status === 'suspended' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {partner.status === 'active' ? 'Ativo' : 
                             partner.status === 'sent_for_signature' ? 'Enviado para Assinatura' : 
                             partner.status === 'suspended' ? 'Suspenso' :
                             'Pendente'}
                          </span>
                        )}
                      </TableCell>
                      {user?.role === 'admin' && (
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadContract(partner.id, partner.name)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title="Baixar Contrato de Parceria"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            {partner.has_rg_cpf && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(partner, 'rg_cpf')}
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="Baixar RG/CPF (será excluído após download)"
                              >
                                <Download className="w-4 h-4" />
                                <span className="text-xs ml-1">RG</span>
                              </Button>
                            )}
                            {partner.has_energy_bill && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(partner, 'energy_bill')}
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                title="Baixar Fatura de Energia (será excluído após download)"
                              >
                                <Download className="w-4 h-4" />
                                <span className="text-xs ml-1">Fatura</span>
                              </Button>
                            )}
                            {partner.has_cnpj_card && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(partner, 'cnpj_card')}
                                className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50"
                                title="Baixar Cartão CNPJ (será excluído após download)"
                              >
                                <Download className="w-4 h-4" />
                                <span className="text-xs ml-1">CNPJ</span>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(partner)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Editar Parceiro"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-partner-${partner.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(partner.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Deletar Parceiro"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {(() => {
                const filteredPartners = partners.filter(p => 
                  (searchTerm === "" || 
                  p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  p.email?.toLowerCase().includes(searchTerm.toLowerCase())) &&
                  (distributorFilter === "all" || p.distributor === distributorFilter)
                );
                const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);
                
                return (
                  <div className="flex justify-between items-center mt-4">
                    {/* Items per page selector */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Itens por página:</span>
                      <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                        <SelectTrigger className="w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-gray-500">
                        ({filteredPartners.length} registros)
                      </span>
                    </div>
                    
                    {/* Pagination controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </Button>
                        <span className="text-sm text-gray-600">
                          Página {currentPage} de {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Próxima
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete All Partners Confirmation Dialog */}
      <Dialog open={deleteAllConfirm} onOpenChange={setDeleteAllConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Deletar Todos os Parceiros
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Esta ação irá excluir <strong>TODOS</strong> os parceiros do sistema.
              <br /><br />
              <span className="text-red-600 font-semibold">Esta ação NÃO pode ser desfeita!</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteAllConfirm(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteAllPartners}
              className="bg-red-600 hover:bg-red-700"
            >
              Confirmar Exclusão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}